import React, { ComponentType, ReactNode, useState } from 'react';
import { getIn } from 'seamless-immutable';
import { getWarrantyUrl } from '@catalogo/core-helpers/url';
import { withMetrics } from '@catalogo/core-metrics';
import compose from 'lodash/fp/compose';
import {
  fmtMainProduct,
  fmtExtraProducts,
  toggleSelectedSku,
  fmtBuyTogetherProducts,
  handleClickBuyButton,
} from '../helpers/buy-together';
import { getPayments } from '../payment/payments';
import { useProduct } from '../hooks/product-buy-together';

type ObjectType = {
  [key: string]: string;
};

type Result = {
  seller: {
    id: string;
    name: string;
  };
};

type ProductType = {
  id: number | string;
};

interface WithComboBuyTogetherProps {
  children: ReactNode;
  currentSku: {
    id: string;
    offers: {
      result: Result[];
    };
  };
  publication: ObjectType[];
  product: ObjectType;
  experiment: boolean;
  categoryUrl: string;
  dispatchMetrics(): void;
}

export const withComboBuyTogether = (Component: ComponentType) => ({
  currentSku,
  publication,
  product,
  dispatchMetrics,
  experiment,
  categoryUrl,
  ...props
}: WithComboBuyTogetherProps) => {
  const [selectedSku, setSelectedSku] = useState<ObjectType>({});
  const products = useProduct(publication);
  const offer = getIn(currentSku, ['offers', 'result', '0']);

  if (!offer || !products.length || !publication) {
    return null;
  }

  const creditCardPriceWithDiscount = getIn(offer, ['bestPaymentOption', 'minQuantity', '0', 'value']);
  const extra = fmtExtraProducts(products, selectedSku);
  const mainProduct = fmtMainProduct(currentSku, product);
  const { mainPayment } = getPayments(offer);

  const productsFormated = fmtBuyTogetherProducts(
    products,
    selectedSku,
    toggleSelectedSku(selectedSku, setSelectedSku, dispatchMetrics),
    categoryUrl
  );

  const currentProduct = {
    id: product?.id,
    name: product?.name,
    price: mainPayment?.price,
    quantity: 1,
    category: categoryUrl,
    sku: currentSku?.id,
    sellerId: currentSku?.offers?.result?.[0]?.seller?.id,
    sellerName: currentSku?.offers?.result?.[0]?.seller?.name,
    isFromBuyTogether: true,
  };

  const currentSelected = productsFormated?.filter((product: ProductType) => selectedSku[product.id] && product) ?? [];

  const comboBuyTogetherProps = {
    rating: getIn(mainProduct, ['rating'], null),
    mainProduct,
    selectedSku,
    products: productsFormated,
    title: getIn(publication, ['title']),
    url: getWarrantyUrl(product.id, currentSku, '', 1, extra),
    totalValue: extra.reduce(
      (acc: ObjectType, curr: ObjectType) => acc + curr.salesPrice,
      creditCardPriceWithDiscount ? creditCardPriceWithDiscount : getIn(offer, ['bestPaymentOption', 'price'])
    ),
    totalProducts: extra.length + 1,
    prime: getIn(offer, ['stamps', 'prime'], false),
    international: getIn(offer, ['currency'], '') === 'USD',
    mainPayment,
    onClickBuyButton: handleClickBuyButton(dispatchMetrics, getWarrantyUrl(product.id, currentSku, '', 1, extra), [
      currentProduct,
      ...currentSelected,
    ]),
  };

  return <Component {...comboBuyTogetherProps} {...props} />;
};

export default compose(withMetrics, withComboBuyTogether);
